import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PackagePlus, ArrowLeft, Plus, Trash2, Search, X, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'

type Prod = { productId: string; sku: string; name: string; unitsPerBox: number | null; saldo: number }
type Pallet = { id: string; boxes: string; lote: string; validade: string }
type Row = { id: string; productId: string; sku: string; name: string; unitsPerBox: number; totalUn: string; pallets: Pallet[] }

const uid = () => Math.random().toString(36).slice(2, 9)

/** Aceita AAAA-MM-DD, DD/MM/AAAA e MM/AAAA (→ último dia do mês). */
function parseDate(s: string): string | undefined {
  const t = (s ?? '').trim()
  let m: RegExpMatchArray | null
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  if ((m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
  if ((m = t.match(/^(\d{1,2})\/(\d{4})$/))) {
    const y = +m[2], mo = +m[1]; const last = new Date(y, mo, 0).getDate()
    return `${y}-${String(mo).padStart(2, '0')}-${String(last).padStart(2, '0')}`
  }
  return undefined
}

export function RecebimentoPaletesPage() {
  const tenantId = useActiveTenant()
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2>(1)
  const [nf, setNf] = useState('')
  const [orderNumber, setOrderNumber] = useState('')
  const [defBoxes, setDefBoxes] = useState('')
  const [defLote, setDefLote] = useState('')
  const [defValidade, setDefValidade] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [term, setTerm] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const { data: prods = [] } = useQuery<Prod[]>({
    queryKey: ['count-sheet', tenantId],
    queryFn: async () => (await api.get('/stock/count-sheet', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const matches = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return []
    const usados = new Set(rows.map(r => r.productId))
    return prods.filter(p => !usados.has(p.productId) && (p.sku.toLowerCase().includes(t) || p.name.toLowerCase().includes(t))).slice(0, 8)
  }, [term, prods, rows])

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  function addSku(p: Prod) {
    setRows(rs => [...rs, { id: uid(), productId: p.productId, sku: p.sku, name: p.name, unitsPerBox: p.unitsPerBox ?? 0, totalUn: '', pallets: [] }])
    setTerm('')
  }
  function removeRow(id: string) { setRows(rs => rs.filter(r => r.id !== id)) }
  function patchRow(id: string, patch: Partial<Row>) { setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r)) }
  function novoPalete(): Pallet { return { id: uid(), boxes: defBoxes, lote: defLote, validade: defValidade } }
  function addPalete(rowId: string, n = 1) {
    setRows(rs => rs.map(r => r.id === rowId ? { ...r, pallets: [...r.pallets, ...Array.from({ length: n }, novoPalete)] } : r))
  }
  function patchPalete(rowId: string, palId: string, patch: Partial<Pallet>) {
    setRows(rs => rs.map(r => r.id === rowId ? { ...r, pallets: r.pallets.map(p => p.id === palId ? { ...p, ...patch } : p) } : r))
  }
  function removePalete(rowId: string, palId: string) {
    setRows(rs => rs.map(r => r.id === rowId ? { ...r, pallets: r.pallets.filter(p => p.id !== palId) } : r))
  }

  const unDoPalete = (r: Row, p: Pallet) => (Math.round(Number(p.boxes) || 0)) * r.unitsPerBox
  const unContadas = (r: Row) => r.pallets.reduce((s, p) => s + unDoPalete(r, p), 0)
  const totalPaletes = rows.reduce((s, r) => s + r.pallets.length, 0)
  const totalUnPal = rows.reduce((s, r) => s + unContadas(r), 0)
  const rowsComTotal = rows.filter(r => Math.round(Number(r.totalUn) || 0) > 0)

  async function importar() {
    const items = rows.map(r => ({
      productId: r.productId,
      totalExpected: Math.round(Number(r.totalUn) || 0),
      unitsPerBox: r.unitsPerBox || undefined,
      pallets: r.pallets.filter(p => (Number(p.boxes) || 0) > 0).map(p => ({ boxes: Math.round(Number(p.boxes) || 0), lote: p.lote.trim(), validade: parseDate(p.validade) })),
    }))
    if (!items.length || busy) return
    if (!window.confirm(`Criar a ordem de recebimento da NF ${nf || '—'} e receber ${totalPaletes} palete(s) (${totalUnPal} un)?`)) return
    setBusy(true); setMsg(null)
    try {
      const { data: rr } = await api.post('/inbound/receive-order', { tenantId, nf: nf.trim() || undefined, orderNumber: orderNumber.trim() || undefined, items })
      setMsg(`Ordem ${rr.orderNumber} criada · ${rr.paletesCriados} palete(s), ${rr.unidades} un.${rr.divergente ? ' ⚠️ com divergência (recebido ≠ esperado) — veja em Recebimento.' : ' ✓ sem divergência.'}`)
      setRows([]); setStep(1)
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao importar recebimento')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-5 pb-24">
      <div className="flex items-center gap-3">
        <button onClick={() => step === 2 ? setStep(1) : navigate('/inbound')} className="text-gray-400 hover:text-gray-700"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">Recebimento por ordem</h2>
          <p className="text-sm text-gray-500">
            <span className={step === 1 ? 'font-semibold text-blue-700' : ''}>1. Nota (totais por SKU)</span>
            <ArrowRight className="mx-1.5 inline h-3.5 w-3.5 text-gray-300" />
            <span className={step === 2 ? 'font-semibold text-blue-700' : ''}>2. Paletes (lote/validade)</span>
          </p>
        </div>
      </div>

      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{msg}</div>}

      {step === 1 ? (
        <>
          {/* Passo 1: cabeçalho da nota + totais por SKU */}
          <Card>
            <CardContent className="flex flex-wrap items-end gap-4 py-4">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">NF do recebimento</label>
                <input value={nf} onChange={e => setNf(e.target.value)} placeholder="ex.: 12345"
                  className="h-10 w-40 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Nº da ordem (opcional)</label>
                <input value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="auto: REC-NF"
                  className="h-10 w-44 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
              </div>
            </CardContent>
          </Card>

          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Adicionar SKU da nota (código ou nome)"
              className="h-11 w-full rounded-lg border border-gray-300 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none" />
            {matches.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                {matches.map(p => (
                  <button key={p.productId} onClick={() => addSku(p)}
                    className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-left last:border-0 hover:bg-blue-50">
                    <span className="font-mono text-sm font-semibold text-gray-800">{p.sku}</span>
                    <span className="flex-1 truncate text-sm text-gray-600">{p.name}</span>
                    <span className="text-xs text-gray-400">{p.unitsPerBox ? `${p.unitsPerBox}/cx` : 'sem un/cx'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-gray-400">Adicione os SKUs da nota e o total de cada um.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">SKU</th>
                      <th className="px-4 py-2 text-left font-medium">Produto</th>
                      <th className="px-4 py-2 text-right font-medium">Un/cx</th>
                      <th className="px-4 py-2 text-right font-medium">Total (un)</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map(r => (
                      <tr key={r.id}>
                        <td className="px-4 py-2 font-mono font-semibold text-blue-800">{r.sku}</td>
                        <td className="px-4 py-2 text-gray-600">{r.name}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{r.unitsPerBox || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <input value={r.totalUn} onChange={e => patchRow(r.id, { totalUn: e.target.value.replace(/\D/g, '') })} placeholder="0"
                            className="h-8 w-28 rounded border border-gray-300 px-2 text-right text-sm focus:border-blue-500 focus:outline-none" />
                        </td>
                        <td className="px-4 py-2 text-right"><button onClick={() => removeRow(r.id)} className="text-gray-300 hover:text-red-500"><X className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button size="lg" disabled={rowsComTotal.length === 0} onClick={() => setStep(2)}>
              Avançar para paletes <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Passo 2: paletes por SKU */}
          <Card>
            <CardContent className="flex flex-wrap items-end gap-4 py-4">
              <span className="pb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Padrão de novo palete</span>
              <div>
                <label className="mb-1 block text-[11px] text-gray-400">Caixas</label>
                <input value={defBoxes} onChange={e => setDefBoxes(e.target.value.replace(/\D/g, ''))} placeholder="ex.: 100"
                  className="h-10 w-24 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] text-gray-400">Lote</label>
                <input value={defLote} onChange={e => setDefLote(e.target.value)} placeholder="lote"
                  className="h-10 w-32 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] text-gray-400">Validade</label>
                <input value={defValidade} onChange={e => setDefValidade(e.target.value)} placeholder="MM/AAAA"
                  className="h-10 w-28 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
              </div>
              <span className="pb-2 text-[11px] text-gray-400">Novos paletes já vêm com esses valores — ajuste o que mudar.</span>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {rows.map(r => {
              const contadas = unContadas(r)
              const total = Math.round(Number(r.totalUn) || 0)
              const diff = total ? contadas - total : 0
              return (
                <Card key={r.id}>
                  <CardContent className="space-y-3 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-sm font-bold text-blue-800">{r.sku}</span>
                      <span className="flex-1 truncate text-sm text-gray-700">{r.name}</span>
                      <span className="text-xs text-gray-400">{r.unitsPerBox ? `${r.unitsPerBox} un/cx` : 'sem un/cx'} · total nota {total} un</span>
                    </div>

                    {r.pallets.length > 0 && (
                      <table className="w-full text-xs">
                        <thead className="text-gray-500">
                          <tr className="border-b border-gray-200">
                            <th className="px-2 py-1 text-left font-medium">#</th>
                            <th className="px-2 py-1 text-left font-medium">Caixas</th>
                            <th className="px-2 py-1 text-left font-medium">Lote</th>
                            <th className="px-2 py-1 text-left font-medium">Validade</th>
                            <th className="px-2 py-1 text-right font-medium">Un</th>
                            <th className="px-2 py-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {r.pallets.map((p, i) => (
                            <tr key={p.id} className="border-b border-gray-100">
                              <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                              <td className="px-2 py-1"><input value={p.boxes} onChange={e => patchPalete(r.id, p.id, { boxes: e.target.value.replace(/\D/g, '') })} className="h-8 w-20 rounded border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none" /></td>
                              <td className="px-2 py-1"><input value={p.lote} onChange={e => patchPalete(r.id, p.id, { lote: e.target.value })} className="h-8 w-32 rounded border border-gray-300 px-2 text-sm focus:border-blue-500 focus:outline-none" /></td>
                              <td className="px-2 py-1"><input value={p.validade} onChange={e => patchPalete(r.id, p.id, { validade: e.target.value })} placeholder="MM/AAAA" className={'h-8 w-28 rounded border px-2 text-sm focus:border-blue-500 focus:outline-none ' + (p.validade && !parseDate(p.validade) ? 'border-amber-400' : 'border-gray-300')} /></td>
                              <td className="px-2 py-1 text-right font-mono">{unDoPalete(r, p) || '—'}</td>
                              <td className="px-2 py-1 text-right"><button onClick={() => removePalete(r.id, p.id)} className="text-gray-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => addPalete(r.id, 1)}><Plus className="h-4 w-4" /> Palete</Button>
                      <AddN onAdd={n => addPalete(r.id, n)} />
                      <span className="ml-auto text-xs text-gray-500">
                        {r.pallets.length} palete(s) · <b className="text-gray-800">{contadas}</b> un
                        {total ? <> / {total} · {diff === 0 ? <span className="text-emerald-600">bate ✓</span> : <span className={diff > 0 ? 'text-red-600' : 'text-amber-600'}>{diff > 0 ? `sobra ${diff}` : `faltam ${-diff}`}</span>}</> : null}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <div className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 p-3 backdrop-blur">
            <div className="mx-auto flex max-w-5xl items-center gap-3 px-4">
              <Button variant="secondary" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4" /> Voltar aos totais</Button>
              <div className="text-sm text-gray-600"><b className="text-gray-900">{totalPaletes}</b> palete(s) · <b className="text-gray-900">{totalUnPal}</b> un{nf ? <> · NF {nf}</> : null}</div>
              <Button size="lg" className="ml-auto" loading={busy} disabled={totalPaletes === 0} onClick={importar}><PackagePlus className="h-5 w-5" /> Criar ordem + receber</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function AddN({ onAdd }: { onAdd: (n: number) => void }) {
  const [n, setN] = useState('')
  return (
    <div className="flex items-center gap-1">
      <input value={n} onChange={e => setN(e.target.value.replace(/\D/g, ''))} placeholder="N" className="h-8 w-14 rounded border border-gray-300 px-2 text-center text-sm focus:border-blue-500 focus:outline-none" />
      <Button size="sm" variant="ghost" disabled={!(Number(n) > 0)} onClick={() => { onAdd(Number(n)); setN('') }}>+ N paletes iguais</Button>
    </div>
  )
}
